/**
 * Jurisdiction Manager Dashboard
 * ==============================
 * 
 * Enterprise control center for jurisdiction data management.
 * Provides at-a-glance KPIs, quick actions, and links to existing tools.
 * 
 * Features:
 * - Data Health Overview Cards (KPIs)
 * - Data Quality Alerts with links to Health Report
 * - US Coverage Map (interactive)
 * - Recently Updated Jurisdictions
 * - Quick Actions Panel
 * - Distribution Chart (by type)
 * - MLC Jurisdiction Tracker
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import JurisdictionWizardModal from '../components/jurisdiction/JurisdictionWizardModal';
import OpsIntroOverlay from '../components/dashboard/OpsIntroOverlay';
import CSVUploadModal from '../components/jurisdiction/CSVUploadModal';
import {
  Building2,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  Phone,
  Mail,
  FileText,
  Plus,
  Upload,
  Activity,
  RefreshCw,
  Search,
  ExternalLink,
  BarChart3,
  Map,
  AlertCircle,
  XCircle,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { formatDateOnly } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

// TopoJSON source for US states (served from public/)
const GEO_URL = '/states-10m.json';

// FIPS → state abbreviation mapping (used by us-atlas TopoJSON)
const FIPS_TO_STATE = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE',
  '11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA',
  '20':'KS','21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN',
  '28':'MS','29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM',
  '36':'NY','37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI',
  '45':'SC','46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA',
  '54':'WV','55':'WI','56':'WY'
};

const STATE_NAMES = {
  'AL':'Alabama','AK':'Alaska','AZ':'Arizona','AR':'Arkansas','CA':'California',
  'CO':'Colorado','CT':'Connecticut','DE':'Delaware','DC':'District of Columbia',
  'FL':'Florida','GA':'Georgia','HI':'Hawaii','ID':'Idaho','IL':'Illinois',
  'IN':'Indiana','IA':'Iowa','KS':'Kansas','KY':'Kentucky','LA':'Louisiana',
  'ME':'Maine','MD':'Maryland','MA':'Massachusetts','MI':'Michigan','MN':'Minnesota',
  'MS':'Mississippi','MO':'Missouri','MT':'Montana','NE':'Nebraska','NV':'Nevada',
  'NH':'New Hampshire','NJ':'New Jersey','NM':'New Mexico','NY':'New York',
  'NC':'North Carolina','ND':'North Dakota','OH':'Ohio','OK':'Oklahoma','OR':'Oregon',
  'PA':'Pennsylvania','RI':'Rhode Island','SC':'South Carolina','SD':'South Dakota',
  'TN':'Tennessee','TX':'Texas','UT':'Utah','VT':'Vermont','VA':'Virginia',
  'WA':'Washington','WV':'West Virginia','WI':'Wisconsin','WY':'Wyoming'
};

// ── US Coverage Map Component (react-simple-maps) ──
const USCoverageMap = ({ statesCovered }) => {
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const coveredSet = useMemo(() => new Set(statesCovered), [statesCovered]);

  return (
    <div className="relative bg-slate-50 rounded-lg overflow-hidden" data-testid="us-geo-map">
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1000 }}
        width={800}
        height={500}
        style={{ width: '100%', height: 'auto' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const fips = geo.id;
              const stateCode = FIPS_TO_STATE[fips];
              const isCovered = stateCode && coveredSet.has(stateCode);
              const stateName = stateCode ? (STATE_NAMES[stateCode] || stateCode) : geo.properties.name;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isCovered ? '#4f46e5' : '#e5e7eb'}
                  stroke="#ffffff"
                  strokeWidth={0.75}
                  onMouseEnter={(e) => {
                    setHoveredState({ code: stateCode, name: stateName, covered: isCovered });
                    setTooltipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => {
                    setTooltipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={() => setHoveredState(null)}
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: isCovered ? '#3730a3' : '#d1d5db', outline: 'none', cursor: 'pointer' },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {/* Tooltip */}
      {hoveredState && (
        <div
          className="fixed z-50 pointer-events-none px-3 py-1.5 rounded-md text-xs font-medium shadow-lg border"
          style={{
            left: tooltipPos.x + 12,
            top: tooltipPos.y - 32,
            backgroundColor: hoveredState.covered ? '#312e81' : '#374151',
            color: '#ffffff',
            borderColor: hoveredState.covered ? '#4338ca' : '#4b5563',
          }}
          data-testid="map-tooltip"
        >
          <span className="font-semibold">{hoveredState.name}</span>
          {hoveredState.code && <span className="ml-1 opacity-70">({hoveredState.code})</span>}
          <span className="ml-2">{hoveredState.covered ? 'Covered' : 'No Data'}</span>
        </div>
      )}
    </div>
  );
};

export default function JurisdictionManagerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);
  const [mlcJurisdictions, setMlcJurisdictions] = useState([]);
  const [statesCovered, setStatesCovered] = useState([]);
  const [distributionData, setDistributionData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Jun 2026 — Graceful loading state. `slowLoadVisible` lets the user
  // know things are taking longer than expected without ripping the
  // spinner away; `loadError` surfaces a retry CTA so failed loads
  // never spin forever (P1 inspection request, Jun 2026).
  const [slowLoadVisible, setSlowLoadVisible] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    setLoadError(null);
    setSlowLoadVisible(false);

    // Show the "still loading" hint after 5s so users know the request
    // is in flight and the UI hasn't frozen. Cleared in `finally`.
    const slowTimer = setTimeout(() => setSlowLoadVisible(true), 5000);

    // Hard ceiling: if both fetches haven't resolved in 20s, bail out
    // so the spinner never spins indefinitely. Surfaces a Retry CTA.
    const abortCtl = new AbortController();
    const hardTimer = setTimeout(() => abortCtl.abort(), 20000);

    const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // JM-DASH-001 (Feb 2026) — Switched mount-time fetch from the heavy
      // `GET /api/jurisdictions?page_size=5000&active_only=true` call to
      // the dedicated server-side aggregation endpoint
      // `GET /api/jurisdictions/dashboard-summary`. Payload drops from
      // ~MBs to <5KB; counts come from a $facet so they ALWAYS match the
      // Directory total (no client-side page-size capping). The 20s
      // AbortController + 5s slow-hint + Retry CTA below are unchanged.
      // JURISDICTION-HEALTH-CLEANUP-001: the companion `/jurisdictions/health`
      // request (only consumed by the retired Data Health Alerts card) is gone.
      const summaryRes = await fetch(`${API}/api/jurisdictions/dashboard-summary`, { headers, signal: abortCtl.signal });

      if (!summaryRes.ok) {
        throw new Error(`Failed to fetch dashboard data (summary=${summaryRes.status})`);
      }

      const summary = await summaryRes.json();

      const elapsedMs = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - t0;
      // eslint-disable-next-line no-console
      console.info(
        `[JM-Dashboard] summary fetch ok in ${elapsedMs.toFixed(0)}ms ` +
        `(total=${summary?.stats?.total ?? 'n/a'}, ` +
        `cache_age=${summary?.cache_age_seconds ?? 0}s, ` +
        `degraded=${!!summary?.degraded}, ` +
        `payload≈${(JSON.stringify(summary).length / 1024).toFixed(1)}KB)`
      );

      const s = summary.stats || {};

      // Map backend snake_case → existing camelCase consumed by the JSX.
      // Preserves the entire KPI/render contract so no downstream changes
      // are required in this file.
      setStats({
        total: s.total || 0,
        active: s.active || 0,
        inactive: s.inactive || 0,
        mlcRequired: s.mlc_required || 0,
        missingContact: s.missing_contact || 0,
        recentlyUpdated: s.recently_updated_7d || 0,
      });

      // Recently updated — pre-sorted & pre-limited (≤10) server-side.
      // Normalize so the existing JSX (which reads `jurisdiction_name`)
      // keeps working when the server returned only `canonical_name`.
      setRecentlyUpdated(
        (summary.recently_updated || []).map((j) => ({
          ...j,
          jurisdiction_name: j.jurisdiction_name || j.canonical_name,
        }))
      );

      // MLC list — server already filtered & projected. Compute
      // `hasMailingAddress` for the readiness badge.
      setMlcJurisdictions(
        (summary.mlc_jurisdictions || []).map((j) => ({
          ...j,
          jurisdiction_name: j.jurisdiction_name || j.canonical_name,
          hasMailingAddress: !!j.mailing_address,
        }))
      );

      // States covered — derived from server-side by_state aggregation.
      setStatesCovered(
        (summary.by_state || []).map((row) => row.state).filter(Boolean)
      );

      // Distribution — already sorted desc server-side.
      setDistributionData(
        (summary.by_type || [])
          .filter((row) => row.type) // strip nulls in case some rows lack a type
          .map((row) => ({ type: row.type, count: row.count }))
      );

    } catch (error) {
      const isAbort = error?.name === 'AbortError';
      const elapsedMs = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - t0;
      // eslint-disable-next-line no-console
      console.error(
        `[JM-Dashboard] fetch ${isAbort ? 'aborted (hard 20s timeout)' : 'failed'} after ${elapsedMs.toFixed(0)}ms:`,
        error
      );
      setLoadError(
        isAbort
          ? 'Dashboard is taking unusually long to load. The backend may be under heavy load. Please retry in a moment.'
          : (error?.message || 'Failed to load dashboard data')
      );
      toast.error('Failed to load dashboard data');
    } finally {
      clearTimeout(slowTimer);
      clearTimeout(hardTimer);
      setLoading(false);
      setRefreshing(false);
      setSlowLoadVisible(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Quick search handler
  const handleQuickSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/admin/jurisdictions?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateOnly(dateStr);
  };

  // Calculate donut chart segments
  const getDonutSegments = () => {
    if (!distributionData.length) return [];
    const total = distributionData.reduce((sum, d) => sum + d.count, 0);
    let currentAngle = 0;
    const colors = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    
    return distributionData.slice(0, 7).map((item, index) => {
      const percentage = (item.count / total) * 100;
      const angle = (percentage / 100) * 360;
      const segment = {
        type: item.type,
        count: item.count,
        percentage: percentage.toFixed(1),
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: colors[index % colors.length]
      };
      currentAngle += angle;
      return segment;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="jm-dashboard-loading">
        <div className="text-center max-w-md px-4">
          <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
          {slowLoadVisible && (
            <p
              className="text-xs text-amber-600 mt-3"
              data-testid="jm-dashboard-slow-hint"
            >
              Still loading… this is taking longer than usual. The backend
              may be processing a large jurisdiction dataset.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="jm-dashboard-error">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Dashboard couldn&apos;t load
          </h2>
          <p className="text-sm text-gray-600 mb-4" data-testid="jm-dashboard-error-message">
            {loadError}
          </p>
          <Button
            onClick={() => fetchDashboardData(false)}
            data-testid="jm-dashboard-retry-btn"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="jurisdiction-manager-dashboard">
      <OpsIntroOverlay />
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Jurisdiction Manager Dashboard</h1>
              <p className="text-sm text-gray-500 mt-1">Data governance and jurisdiction management control center</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchDashboardData(true)}
                disabled={refreshing}
                data-testid="refresh-dashboard-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-cards">
          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Building2 className="h-8 w-8 text-indigo-600" />
                <span className="text-2xl font-bold text-gray-900">{stats?.total || 0}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Total Jurisdictions</p>
            </CardContent>
          </Card>

          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{stats?.active || 0}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Active</p>
            </CardContent>
          </Card>

          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Mail className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{stats?.mlcRequired || 0}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">MLC Required</p>
            </CardContent>
          </Card>

          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="h-8 w-8 text-amber-600" />
                <span className="text-2xl font-bold text-amber-600">{stats?.missingContact || 0}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Missing Contact</p>
            </CardContent>
          </Card>

          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <XCircle className="h-8 w-8 text-gray-400" />
                <span className="text-2xl font-bold text-gray-500">{stats?.inactive || 0}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Inactive</p>
            </CardContent>
          </Card>

          <Card className="bg-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="h-8 w-8 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">{stats?.recentlyUpdated || 0}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Updated This Week</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Search + Quick Actions Row (JURISDICTION-HEALTH-CLEANUP-001: Data Health Alerts card retired) */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Quick Search */}
          <Card className="bg-white lg:col-span-2" data-testid="quick-search-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5 text-gray-500" />
                Quick Search
              </CardTitle>
              <CardDescription>Find jurisdictions by name, check payable name, or state</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={handleQuickSearch} className="flex gap-2">
                <Input
                  placeholder="Search jurisdictions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                  data-testid="quick-search-input"
                />
                <Button type="submit" data-testid="quick-search-btn">
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </form>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500" data-testid="quick-search-hints">
                <span className="mr-1">Try:</span>
                {['PA', 'NJ', 'Delaware County', 'Township'].map((hint) => (
                  <button
                    key={hint}
                    type="button"
                    onClick={() => navigate(`/admin/jurisdictions?search=${encodeURIComponent(hint)}`)}
                    className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                    data-testid={`quick-search-hint-${hint.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {hint}
                  </button>
                ))}
                <span className="ml-auto text-gray-400">Results open in the Directory</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-white lg:col-span-3" data-testid="quick-actions-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
              <CardDescription>Frequently used jurisdiction management actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  variant="default"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => setShowAddModal(true)}
                  data-testid="add-jurisdiction-btn"
                >
                  <Plus className="h-6 w-6" />
                  <span className="text-sm">Add Jurisdiction</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => setShowUploadModal(true)}
                  data-testid="bulk-upload-btn"
                >
                  <Upload className="h-6 w-6 text-gray-600" />
                  <span className="text-sm">Bulk Upload</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/admin/jurisdictions')}
                  data-testid="directory-btn"
                >
                  <FileText className="h-6 w-6 text-gray-600" />
                  <span className="text-sm">Directory</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map + Distribution Chart Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* US Coverage Map */}
          <Card className="bg-white lg:col-span-2" data-testid="coverage-map">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Map className="h-5 w-5 text-indigo-600" />
                Jurisdiction Coverage Map
              </CardTitle>
              <CardDescription>
                States Covered: <span className="font-semibold text-indigo-600">{statesCovered.length}</span> | 
                States Missing: <span className="font-semibold text-gray-500">{50 - statesCovered.length}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <USCoverageMap statesCovered={statesCovered} />
              {/* Legend */}
              <div className="flex items-center gap-6 mt-3 justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-indigo-600"></div>
                  <span className="text-sm text-gray-600">Covered ({statesCovered.length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gray-200 border border-gray-300"></div>
                  <span className="text-sm text-gray-600">No Data ({50 - statesCovered.length})</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Distribution Chart */}
          <Card className="bg-white" data-testid="distribution-chart">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                By Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {distributionData.slice(0, 6).map((item, index) => {
                  const colors = ['bg-indigo-600', 'bg-blue-500', 'bg-green-500', 'bg-amber-500', 'bg-red-500', 'bg-purple-500'];
                  const percentage = stats?.total ? ((item.count / stats.total) * 100).toFixed(0) : 0;
                  return (
                    <div key={item.type}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="capitalize text-gray-700">{item.type}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${colors[index % colors.length]}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recently Updated + MLC Tracker Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recently Updated */}
          <Card className="bg-white" data-testid="recently-updated">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                Recently Updated
              </CardTitle>
              <CardDescription>Latest jurisdiction changes in the past 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="tf-thead">
                    <tr>
                      <th className="text-left py-2 font-medium text-gray-500">Jurisdiction</th>
                      <th className="text-left py-2 font-medium text-gray-500">State</th>
                      <th className="text-left py-2 font-medium text-gray-500">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentlyUpdated.slice(0, 5).map((j) => (
                      <tr key={j.id} className="border-b hover:bg-gray-50">
                        <td className="py-2">
                          <button
                            onClick={() => navigate(`/admin/jurisdictions?search=${encodeURIComponent(j.jurisdiction_name)}`)}
                            className="text-indigo-600 hover:underline text-left"
                          >
                            {j.jurisdiction_name}
                          </button>
                        </td>
                        <td className="py-2 text-gray-600">{j.state || '—'}</td>
                        <td className="py-2 text-gray-500">{formatDate(j.updated_at || j.created_at)}</td>
                      </tr>
                    ))}
                    {recentlyUpdated.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 text-center text-gray-500">
                          No recent updates
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-3 w-full text-indigo-600"
                onClick={() => navigate('/admin/jurisdictions')}
              >
                View Activity Log
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* MLC Jurisdiction Tracker */}
          <Card className="bg-white" data-testid="mlc-tracker">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                MLC Jurisdiction Tracker
              </CardTitle>
              <CardDescription>Mail-away required jurisdictions and their readiness</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="tf-thead">
                    <tr>
                      <th className="text-left py-2 font-medium text-gray-500">Jurisdiction</th>
                      <th className="text-left py-2 font-medium text-gray-500">State</th>
                      <th className="text-left py-2 font-medium text-gray-500">Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mlcJurisdictions.slice(0, 5).map((j) => (
                      <tr key={j.id} className="border-b hover:bg-gray-50">
                        <td className="py-2">
                          <button
                            onClick={() => navigate(`/admin/jurisdictions?search=${encodeURIComponent(j.jurisdiction_name)}`)}
                            className="text-indigo-600 hover:underline text-left"
                          >
                            {j.jurisdiction_name}
                          </button>
                        </td>
                        <td className="py-2 text-gray-600">{j.state || '—'}</td>
                        <td className="py-2">
                          {j.hasMailingAddress ? (
                            <Badge className="bg-green-100 text-green-700 border-0">Available</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-0">Missing</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                    {mlcJurisdictions.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 text-center text-gray-500">
                          No MLC jurisdictions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {mlcJurisdictions.length > 5 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-3 w-full text-indigo-600"
                  onClick={() => navigate('/admin/jurisdictions?filter=mlc')}
                >
                  View All {mlcJurisdictions.length} MLC Jurisdictions
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Navigation */}
        <Card className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Ready to manage jurisdictions?</h3>
                <p className="text-indigo-200 text-sm">Access the full directory with search, filters, and bulk operations.</p>
              </div>
              <Button 
                variant="secondary"
                onClick={() => navigate('/admin/jurisdictions')}
                data-testid="go-to-directory-btn"
              >
                Open Directory
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Jurisdiction Wizard Modal (reused from Directory) */}
      <JurisdictionWizardModal
        isOpen={showAddModal}
        jurisdiction={null}
        onClose={() => setShowAddModal(false)}
        onSave={async () => {
          setShowAddModal(false);
          toast.success('Jurisdiction created successfully');
          fetchDashboardData(true);
        }}
      />

      {showUploadModal && (
        <CSVUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchDashboardData(true);
          }}
          token={localStorage.getItem('token')}
        />
      )}
    </div>
  );
}
